export const TARGET_FPS_OPTIONS = [24, 15] as const;
export type TargetFps = (typeof TARGET_FPS_OPTIONS)[number];
export type FrameTimingStage = 'pose' | 'segmentation' | 'mask' | 'packing' | 'total';

export interface FrameTimings {
  poseMs: number;
  segmentationMs: number;
  maskMs: number;
  packingMs: number;
  totalMs: number;
}

export interface TimingSummary {
  count: number;
  p50?: number;
  p95?: number;
}

export interface FrameBudgetSnapshot {
  targetFps: TargetFps;
  budgetMs: number;
  sampleCount: number;
  stages: Record<FrameTimingStage, TimingSummary>;
  notice?: string;
  overBudget: boolean;
  autoStepCount: number;
}

export const FRAME_BUDGET_DECISION_SAMPLES = 20;
const HISTORY_LIMIT = 180;

export const frameBudgetMs = (targetFps: TargetFps): number => 1_000 / targetFps;

function percentile(values: readonly number[], fraction: number): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * fraction) - 1))];
}

function summary(values: readonly number[]): TimingSummary {
  return { count: values.length, p50: percentile(values, 0.5), p95: percentile(values, 0.95) };
}

function validTiming(sample: FrameTimings): boolean {
  return Object.values(sample).every((value) => Number.isFinite(value) && value >= 0 && value <= 10_000);
}

export function isTargetFps(value: number): value is TargetFps {
  return TARGET_FPS_OPTIONS.includes(value as TargetFps);
}

/** Rolling timing summaries plus a deterministic iPhone-oriented 24 → 15 fps governor. */
export class FrameBudgetController {
  #targetFps: TargetFps;
  #history: FrameTimings[] = [];
  #decisionWindow: FrameTimings[] = [];
  #nextDueMs?: number;
  #notice?: string;
  #autoStepCount = 0;
  #reportedFifteenFpsOverrun = false;

  constructor(targetFps: TargetFps = 24) {
    this.#targetFps = targetFps;
  }

  get targetFps(): TargetFps {
    return this.#targetFps;
  }

  setTargetFps(targetFps: TargetFps): FrameBudgetSnapshot {
    if (targetFps !== this.#targetFps) {
      this.#targetFps = targetFps;
      this.#history = [];
      this.#decisionWindow = [];
      this.#nextDueMs = undefined;
      this.#reportedFifteenFpsOverrun = false;
      this.#notice = `Target set to ${targetFps} fps · ${frameBudgetMs(targetFps).toFixed(1)} ms budget.`;
    }
    return this.snapshot();
  }

  shouldProcess(timestampMs: number): boolean {
    if (!Number.isFinite(timestampMs)) return false;
    const interval = frameBudgetMs(this.#targetFps);
    if (this.#nextDueMs === undefined || timestampMs + 0.05 >= this.#nextDueMs) {
      if (this.#nextDueMs === undefined || timestampMs - this.#nextDueMs > interval * 4) {
        this.#nextDueMs = timestampMs + interval;
      } else {
        do this.#nextDueMs += interval;
        while (this.#nextDueMs <= timestampMs);
      }
      return true;
    }
    return false;
  }

  add(sample: FrameTimings): FrameBudgetSnapshot {
    if (!validTiming(sample)) return this.snapshot();
    this.#history.push(sample);
    this.#decisionWindow.push(sample);
    if (this.#history.length > HISTORY_LIMIT) this.#history.splice(0, this.#history.length - HISTORY_LIMIT);

    if (this.#decisionWindow.length >= FRAME_BUDGET_DECISION_SAMPLES) {
      const p95 = percentile(this.#decisionWindow.map((entry) => entry.totalMs), 0.95)!;
      const budget = frameBudgetMs(this.#targetFps);
      if (p95 > budget) {
        const previous = this.#targetFps;
        const next = previous === 24 ? 15 : 15;
        if (next !== previous) {
          this.#targetFps = next;
          this.#history = [];
          this.#autoStepCount += 1;
          this.#nextDueMs = undefined;
          this.#notice = `Auto-stepped ${previous} → ${next} fps: p95 ${p95.toFixed(1)} ms exceeded the ${budget.toFixed(1)} ms budget.`;
        } else if (!this.#reportedFifteenFpsOverrun) {
          this.#reportedFifteenFpsOverrun = true;
          this.#notice = `15 fps is still over budget: p95 ${p95.toFixed(1)} ms exceeds ${budget.toFixed(1)} ms.`;
        }
      }
      this.#decisionWindow = this.#targetFps === 15 && this.#reportedFifteenFpsOverrun
        ? this.#decisionWindow.slice(-19)
        : this.#decisionWindow.length > 0 && p95 <= budget
          ? this.#decisionWindow.slice(-19)
          : [];
    }
    return this.snapshot();
  }

  snapshot(): FrameBudgetSnapshot {
    const stages = {
      pose: summary(this.#history.map((entry) => entry.poseMs)),
      segmentation: summary(this.#history.map((entry) => entry.segmentationMs)),
      mask: summary(this.#history.map((entry) => entry.maskMs)),
      packing: summary(this.#history.map((entry) => entry.packingMs)),
      total: summary(this.#history.map((entry) => entry.totalMs)),
    };
    return {
      targetFps: this.#targetFps,
      budgetMs: frameBudgetMs(this.#targetFps),
      sampleCount: this.#history.length,
      stages,
      notice: this.#notice,
      overBudget: (stages.total.p95 ?? 0) > frameBudgetMs(this.#targetFps),
      autoStepCount: this.#autoStepCount,
    };
  }
}
